import React, { useState, useMemo } from 'react';
import EventListItem from '../list/EventListItem';
import ExpandButton from '../shared/ExpandButton';
import MatchEvent from './MatchEvent';
import MultiDatasetMatchEvent from './MultiDatasetMatchEvent';

/**
 * GroupMatchEvent - Renders a group match and its individual match children
 */
function GroupMatchEvent({
  groupMatch,
  allEvents,
  expandedGroupMatches,
  onToggleGroupMatch,
  onEventClick,
  onDeleteClick,
  disabled
}) {
  // Check if expanded from parent
  const isExpanded = expandedGroupMatches.has(groupMatch.eventId);

  // Get all individual matches that belong to this group (both regular and multi-dataset)
  const individualMatches = useMemo(() => {
    return allEvents
      .filter(e =>
        (e.eventType === 'match' || e.eventType === 'multi_dataset_match') &&
        e.metadata?.groupEventId === groupMatch.eventId
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [allEvents, groupMatch.eventId]);

  const handleToggleExpand = () => {
    onToggleGroupMatch(groupMatch.eventId);
  };

  const expandButton = individualMatches.length > 0 ? (
    <ExpandButton
      eventId={groupMatch.eventId}
      isExpanded={isExpanded}
      onToggle={handleToggleExpand}
    />
  ) : null;

  return (
    <>
      {/* Group match header */}
      <EventListItem
        event={groupMatch}
        onClick={onEventClick}
        onDelete={onDeleteClick}
        disabled={disabled}
        expandButton={expandButton}
      />

      {/* Individual matches (only if expanded) */}
      {isExpanded && individualMatches.map(match => {
        if (match.eventType === 'multi_dataset_match') {
          return (
            <MultiDatasetMatchEvent
              key={match.eventId}
              match={match}
              onEventClick={onEventClick}
              onDeleteClick={onDeleteClick}
              disabled={disabled}
            />
          );
        }
        return (
          <MatchEvent
            key={match.eventId}
            match={match}
            onEventClick={onEventClick}
            onDeleteClick={onDeleteClick}
            disabled={disabled}
          />
        );
      })}
    </>
  );
}

export default React.memo(GroupMatchEvent);
